import { type FormEvent, useCallback, useEffect, useState } from "react";
import { type Level, type MyOrg, type OrgDetail, type Team, orgsApi } from "../../lib/api/orgs.js";
import "./Orgs.css";

const LEVELS: Level[] = ["exact", "city", "country", "hidden"];

function OrgPanel({ slug, onChanged }: { slug: string; onChanged: () => void }) {
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [teamName, setTeamName] = useState("");

  const load = useCallback(() => {
    orgsApi.detail(slug).then((r) => setDetail(r.success && r.data ? r.data : null));
  }, [slug]);
  useEffect(() => load(), [load]);

  if (!detail) return null;

  async function setCeiling(level: Level) {
    await orgsApi.patch(slug, { visibility_ceiling: level });
    load();
    onChanged();
  }
  async function addTeam(e: FormEvent) {
    e.preventDefault();
    if (!teamName.trim() || !detail) return;
    await orgsApi.teamCreate(detail.org.id, teamName.trim());
    setTeamName("");
    load();
  }

  return (
    <div className="org__panel">
      <label className="org__field">
        <span>Coworkers see at most</span>
        <select
          aria-label="visibility ceiling"
          value={detail.org.visibility_ceiling}
          onChange={(e) => setCeiling(e.target.value as Level)}
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>

      <div className="org__members">
        <h3>Members ({detail.members.length})</h3>
        <ul>
          {detail.members.map((m) => (
            <li key={m.id}>
              {m.name} <span className="org__role">{m.role}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="org__teams">
        <h3>Teams</h3>
        <ul>
          {detail.teams.map((t: Team) => (
            <li key={t.id}>{t.name}</li>
          ))}
        </ul>
        <form className="org__add-team" onSubmit={addTeam}>
          <input
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="New team"
            aria-label="new team name"
          />
          <button type="submit" className="btn btn-primary" disabled={!teamName.trim()}>
            Add
          </button>
        </form>
      </div>
    </div>
  );
}

export function Orgs() {
  const [orgs, setOrgs] = useState<MyOrg[]>([]);
  const [name, setName] = useState("");
  const [domains, setDomains] = useState("");
  const [ceiling, setCeiling] = useState<Level>("city");
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    orgsApi.listMine().then((r) => setOrgs(r.success && r.data ? r.data.orgs : []));
  }, []);
  useEffect(() => load(), [load]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = await orgsApi.create(name.trim(), domains.trim(), ceiling);
    if (res.success) {
      setName("");
      setDomains("");
      load();
    }
  }

  return (
    <section className="org">
      <h1 className="org__h1">Organizations</h1>
      <p className="org__lead">
        Bring coworkers together — with a visibility ceiling that caps (never forces) what they see.
      </p>

      <form className="org__create" onSubmit={create}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Org name"
          aria-label="org name"
          maxLength={80}
        />
        <input
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
          placeholder="Allowed email domains (acme.com, ...)"
          aria-label="allowed domains"
        />
        <label className="org__field">
          <span>Visibility ceiling</span>
          <select
            aria-label="new org ceiling"
            value={ceiling}
            onChange={(e) => setCeiling(e.target.value as Level)}
          >
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
          Create org
        </button>
      </form>

      <ul className="org__list">
        {orgs.map((o) => (
          <li key={o.id} className="org__item">
            <button
              type="button"
              className="org__item-head"
              onClick={() => setOpen(open === o.slug ? null : o.slug)}
            >
              <span className="org__name">{o.name}</span>
              <span className="org__meta">ceiling: {o.visibility_ceiling}</span>
            </button>
            {open === o.slug && <OrgPanel slug={o.slug} onChanged={load} />}
          </li>
        ))}
        {orgs.length === 0 && <p className="org__muted">No orgs yet.</p>}
      </ul>
    </section>
  );
}
